import { useRef, useEffect, useState } from 'react';
import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import { Claim } from '@scale/shared/types/queue';
import { Divider, Tooltip } from '@scale/scaleui';
import { useTimerData } from '../../withV2Timer';
import { useNumberOfTurns, useChatConfig } from '../Chat/hooks/ChatStore.hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getMaxPayableTime, getRemainingTimeWarningThresholdMinutes } from './utils';
import { toast } from 'react-toastify';
import ToolTipText from 'react-scale/components/Expert/Earnings/components/TooltipText';
import { usePauseCapability } from '../common/PauseCapabilityHook';
import { useClientTimer, ClientTimer } from 'react-scale/hooks/useClientTimer';
import { getTimerLoopIntervalSecs } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { formatTime, TIME_FORMAT_PRESETS } from '@scale/shared/utils/dateUtils';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';

type InfoTimeSpentProps = {
  claim?: Claim;
  visible?: boolean;
  taskId?: string;
  taskType?: TaskType;
  projectId?: string;
  assignment?: AssignmentJson;
  isSubmittingTask?: boolean;
};

function usePrevious<T>(value: T) {
  const previousValue = useRef<T>();
  const newValue = useRef<T>();

  if (newValue.current !== value) {
    previousValue.current = newValue.current;
    newValue.current = value;
  }

  return previousValue.current;
}

// if base submission time is less than this, always show seconds in time spent timer
const LOW_SUBMISSION_TIME_THRESHOLD_MINUTES = 5;

export const InfoTimeSpent = ({
  claim,
  taskId,
  taskType,
  projectId,
  assignment,
  isSubmittingTask,
}: InfoTimeSpentProps) => {
  const { payableTime, claimMinutes } = useClaimTime(claim, projectId, assignment);

  const config = useChatConfig();

  const [timerData] = useTimerData();
  const numOfTurns = useNumberOfTurns();

  const maxPayableTimeMinutes = getMaxPayableTime(config, numOfTurns, payableTime);
  const previousMaxPayableTimeMinutes = usePrevious(maxPayableTimeMinutes);

  // Get pause state and task type for client timer
  const { isPaused } = usePauseCapability(taskId ?? '');
  // Get proper timer interval for this task type
  const tickIntervalSeconds = getTimerLoopIntervalSecs(taskType);

  // Use client active timer for real-time display
  const clientTimer: ClientTimer = useClientTimer({
    serverTimeSpentSeconds: timerData.serverTimeSpent,
    tickIntervalSeconds,
    isPaused,
    isSubmittingTask,
  });

  const { clientTimeSpentSeconds, isStale } = clientTimer;

  // Show toast message when additional time is added per turn
  useEffect(() => {
    if (
      !payableTime?.additionalMinutesPerTurn ||
      !previousMaxPayableTimeMinutes ||
      maxPayableTimeMinutes <= previousMaxPayableTimeMinutes
    ) {
      return;
    }

    toast(
      <div className="flex">
        <p className="font-medium text-xs">
          <strong>{payableTime.additionalMinutesPerTurn ?? 0} minutes added to tasking time</strong>
          <br />
          You received {payableTime.additionalMinutesPerTurn ?? 0} minutes of additional tasking
          time for this turn.
        </p>
      </div>,
      {
        position: 'top-left',
        autoClose: 10000,
        theme: 'dark',
      },
    );

    // Reset warning state since user got additional time with new threshold
    if (taskId) {
      setHasShownActiveTimeWarning(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  }, [
    maxPayableTimeMinutes,
    previousMaxPayableTimeMinutes,
    payableTime?.additionalMinutesPerTurn,
    taskId,
  ]);

  // Calculate active tasking time calculations
  const activeTaskingTimeSeconds = maxPayableTimeMinutes * 60;

  // Calculate warning threshold for active tasking time (use total time for moving target warnings)
  const activeTimeByMinTurnsWarningThresholdMinutes =
    getRemainingTimeWarningThresholdMinutes(maxPayableTimeMinutes);

  // Business logic calculations (warnings) - use server time
  const remainingActiveTaskingTimeByMinTurnsServerSeconds = Math.max(
    0,
    activeTaskingTimeSeconds - timerData.serverTimeSpent,
  );
  const remainingActiveTaskingTimeByMinTurnsServerMinutes =
    remainingActiveTaskingTimeByMinTurnsServerSeconds / 60;

  // Warning trigger - based on active tasking time and threshold
  const warningTrigger =
    activeTimeByMinTurnsWarningThresholdMinutes !== null &&
    remainingActiveTaskingTimeByMinTurnsServerMinutes <=
      activeTimeByMinTurnsWarningThresholdMinutes;

  const isLowSubmissionTime = claimMinutes < LOW_SUBMISSION_TIME_THRESHOLD_MINUTES;

  // Use client time for display string - show seconds when low time left, hide when lots of time left
  const timeSpentStr = formatTime(
    clientTimeSpentSeconds,
    warningTrigger || isLowSubmissionTime
      ? // low time -> show time spent with live seconds
        TIME_FORMAT_PRESETS.TIME_SPENT
      : // plenty of time -> only show up to live minutes
        TIME_FORMAT_PRESETS.TIME_SPENT_NO_SECONDS,
  );

  const maxPayableTimeSeconds = maxPayableTimeMinutes * 60;
  const maxPayableTimeStr = formatTime(maxPayableTimeSeconds, TIME_FORMAT_PRESETS.MAX_PAYABLE_TIME);

  // Use client time for display calculations (use total time including additional turns)
  const remainingActiveTaskingTimeClientSeconds = Math.max(
    0,
    activeTaskingTimeSeconds - clientTimeSpentSeconds,
  );

  // Exceeded time calculation (for display) - use client time and total time
  const exceededTimeClientSeconds = Math.max(0, clientTimeSpentSeconds - activeTaskingTimeSeconds);

  // State to track if we've already shown the warning to prevent spam (per taskId)
  const [hasShownActiveTimeWarning, setHasShownActiveTimeWarning] = useState(new Set<string>());

  // Show warning when threshold reached for remaining active tasking time
  useEffect(() => {
    if (
      !taskId ||
      hasShownActiveTimeWarning.has(taskId) ||
      !warningTrigger ||
      remainingActiveTaskingTimeByMinTurnsServerMinutes <= 0
    ) {
      // already shown or not yet time to show
      return;
    }

    setHasShownActiveTimeWarning(prev => new Set([...prev, taskId]));

    const timeLeftString =
      remainingActiveTaskingTimeByMinTurnsServerMinutes < 1
        ? 'less than 1'
        : `${Math.round(remainingActiveTaskingTimeByMinTurnsServerMinutes)}`;

    toast(
      <div className="flex">
        <p className="font-medium text-xs">
          <strong>You have {timeLeftString} min to submit this task</strong>
          <br />
          You can continue working after the timer ends at your exceeded time rate to finalize this
          task if necessary. Being efficient with your time affects your quality rating.
        </p>
      </div>,
      {
        position: 'top-left',
        autoClose: 10000,
        theme: 'dark',
      },
    );
  }, [
    taskId,
    hasShownActiveTimeWarning,
    warningTrigger,
    remainingActiveTaskingTimeByMinTurnsServerMinutes,
  ]);

  if (!claim || !payableTime?.maxPayableMinutes) {
    return null;
  }

  return (
    <div className="flex items-center gap-4 font-normal text-xs text-neutral-700">
      <Tooltip
        delayDuration={0}
        text={
          <ToolTipText
            text={
              <div className="flex flex-col gap-2">
                <span>
                  Remaining Active Tasking Time:{' '}
                  {formatTime(
                    remainingActiveTaskingTimeClientSeconds,
                    TIME_FORMAT_PRESETS.REMAINING_TIME,
                  )}
                </span>
                {exceededTimeClientSeconds > 0 && (
                  <>
                    <span>
                      Exceeded Time:{' '}
                      {formatTime(exceededTimeClientSeconds, TIME_FORMAT_PRESETS.REMAINING_TIME)}
                    </span>
                    <Divider />
                    <span className="text-gray-400">
                      Exceeded Time is the amount of time you have exceeded from the Tasking Time
                      Limit.
                    </span>
                  </>
                )}
              </div>
            }
            width={300}
          />
        }
        target={
          <div
            className={`flex gap-1 items-center ${
              warningTrigger ? `text-danger-500` : isStale ? 'text-warning-500' : 'text-neutral-700'
            }`}
          >
            <FontAwesomeIcon
              icon={[
                'fas',
                `${warningTrigger ? 'triangle-exclamation' : isStale ? 'wifi-slash' : 'clock'}`,
              ]}
            />
            {timeSpentStr} / {maxPayableTimeStr}
            {isStale && <span className="text-xs opacity-75">*</span>}
          </div>
        }
      />
    </div>
  );
};
